import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Contact.scss';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    contactType: '',
    position: '',
    resume: null,
    service: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    if (submitStatus) { // For both success and error
      const timer = setTimeout(() => {
        setSubmitStatus(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [submitStatus]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 5 * 1024 * 1024) {
      alert('File size exceeds 5MB limit');
      return;
    }
    setFormData({
      ...formData,
      resume: file
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();
      
      // Append all form fields to FormData
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('contactType', formData.contactType);
      formDataToSend.append('position', formData.position);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);
      
      // Append the file if it exists
      if (formData.resume) {
        formDataToSend.append('resume', formData.resume);
      }

      const response = await fetch('http://localhost:5000/api/contact', {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        // Reset form after successful submission
        setFormData({
          fullName: '',
          contactType: '',
          position: '',
          resume: null,
          service: '',
          email: '',
          message: ''
        });
        // Scroll to the success message
        setTimeout(() => {
          const successElement = document.querySelector('.alert.success');
          if (successElement) {
            successElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="container">
          <h1>Get In Touch</h1>
          <p className="hero-text">
            Ready to transform your business with our innovative solutions? We'd love to hear from you and discuss how we can help you achieve your goals.
          </p>
        </div>
      </section>

      <div className="contact-container">
        <div className="contact-form-section">
          <h2>Send Us a Message</h2>
          
          {submitStatus === 'success' && (
            <div className="alert success">
              <h3>Message sent successfully!</h3>
              <p>Thank you for contacting us. We'll get back to you soon.</p>
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="alert error">
              <h3>Failed to send message</h3>
              <p>Please try again or contact us directly at simakitpl@gmail.com</p>
            </div>
          )}

          <form 
            className="contact-form"
            encType="multipart/form-data"
            onSubmit={handleSubmit}
          >
            <div className="form-group">
              <label htmlFor="fullName">Full Name *</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                placeholder="Enter Name"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="contactType">You are contacting as *</label>
              <select
                id="contactType"
                name="contactType"
                value={formData.contactType}
                onChange={handleChange}
                required
              >
                <option value="">Select an option</option>
                <option value="client">Potential Client</option>
                <option value="job-applicant">Job Applicant</option>
                <option value="partner">Business Partner</option>
                <option value="other">Other</option>
              </select>
            </div>

            {formData.contactType === 'job-applicant' && (
              <>
                <div className="form-group job-applicant-fields">
                  <label htmlFor="position">Position Applying For *</label>
                  <input
                    type="text"
                    id="position"
                    name="position"
                    placeholder="Enter position name"
                    value={formData.position}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group job-applicant-fields">
                  <label htmlFor="resume">Upload Resume (PDF/DOC) *</label>
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                  <small>Max file size: 5MB</small>
                </div>
              </>
            )}

            <div className="form-group">
              <label htmlFor="service">Service of Interest</label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
              >
                <option value="">Select a service</option>
                <option value="dms">DMS Services</option>
                <option value="it">IT Solutions</option>
                <option value="gis">GIS Mapping</option>
                <option value="consulting">Consulting</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="email@email.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                placeholder="Tell us about your project or how we can help you..."
                value={formData.message}
                onChange={handleChange}
                required
              ></textarea>
            </div>

            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>

        <div className="contact-info-section">
          <div className="contact-info">
            <h2>Contact Information</h2>
            <p>
              Have questions or ready to get started? We're just a message or call away from helping you achieve your goals.
            </p>

            <div className="info-block">
              <h3>Office Address</h3>
              <address>
                SIMAK IT Private Limited, 3rd Floor, Datta Palace, Gandhi Chowk Amravati,<br />
                Maharashtra, India.
              </address>
              <Link to="https://www.google.com/maps/dir/?api=1&destination=Datta+Palace,Gandhi+Chowk,Amravati,Maharashtra" 
                target="_blank"
                rel="noopener noreferrer"
                className="link-btn">Get Directions</Link>
            </div>

            <div className="info-block">
              <h3>Phone Number</h3>
              <p>+91 9284354121</p>
              <Link to="tel:+919284354121" className="link-btn">Call Us</Link>
            </div>

            <div className="info-block">
              <h3>Email Address</h3>
              <p>jobs@simakit.com</p>
              <Link to="mailto:simakitpl@gmail.com" className="link-btn">Send Email</Link>
            </div>

            <div className="info-block">
              <h3>Business Hours</h3>
              <p>Monday - Saturday<br />10:00 AM - 6:00 PM</p>
            </div>

            <div className="info-block">
              <h3>Careers</h3>
              <p>Interested in joining our team?</p>
              <Link to="/careers" className="link-btn">View Open Positions</Link>
            </div>
          </div>
        </div>
      </div>

      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <p className="faq-subtitle">Quick answers to common questions about our services</p>

          <div className="faq-item">
            <h3>What types of GIS mapping services do you offer?</h3>
            <p>
              We specialize in comprehensive GIS solutions including 2D to 3D building asset mapping, cadastral map digitization, telecom tower mapping, and custom basemap creation. Our services cover data collection, conversion, analysis, and visualization for various industries.
            </p>
          </div>

          <div className="faq-item">
            <h3>How do you convert 2D building assets to 3D models?</h3>
            <p>
              We use advanced techniques including Layer 3D to Feature Class conversion and specialized software tools to transform 2D z-aware features into detailed 3D multipatch and 3D object feature layers. This enables enhanced visualization and analysis for urban planning and emergency management.
            </p>
          </div>

          <div className="faq-item">
            <h3>What's involved in your cadastral map digitization process?</h3>
            <p>
              Our cadastral digitization includes high-resolution scanning, georeferencing with real-world coordinates, data extraction, and integration with GIS platforms. We ensure accurate representation of property boundaries and ownership details for modern land management systems.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;